import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, ChevronDown, Search,
  Building2, Library, Receipt, Globe, Shield, Share2,
  TrendingUp, Wrench, Sparkles, HelpCircle, Image as ImageIcon, Users, Play,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import DisclaimerBar from "@/components/DisclaimerBar";
import DemoDialog from "@/components/DemoDialog";

// Each item is grouped into a category for the side-rail filter.
// answer can include light JSX (lists, strong, links) — no full HTML.
const FAQ = [
  // --- General -----------------------------------------------------
  {
    cat: "General",
    icon: Sparkles,
    q: "What is FlipCost?",
    a: (
      <>
        FlipCost is a renovation <strong>decision engine</strong> for property
        investors and homeowners. It estimates renovation cost room-by-room,
        calculates profit, ROI and margin for flips, and tracks live spend
        against budget. It is not a contractor quoting tool.
      </>
    ),
  },
  {
    cat: "General",
    icon: HelpCircle,
    q: "Can I try it before I pay?",
    a: (
      <>
        Yes — there's a <strong>full live demo</strong> available everywhere
        without signing up or paying. Look for the green{" "}
        <strong>"Try the Live Demo"</strong> button on the landing page,
        pricing page, this FAQ, the How-it-works page, and even the sign-in
        screen. The demo loads a pre-built Cape Town flip and lets you edit
        the Overview, add Rooms, build Extensions, layer in Exterior, Systems
        and Soft Costs — every dashboard tab is unlocked. Saving the project
        and exporting a PDF report require a paid Single Project unlock
        (R280 one-off, buy more as needed).
      </>
    ),
  },
  {
    cat: "General",
    icon: HelpCircle,
    q: "Who is it for?",
    a: (
      <>
        Three goals are supported and the dashboard re-frames itself for each:
        <ul className="list-disc pl-5 mt-2 space-y-1 text-[#57534E]">
          <li><strong>Flip for profit</strong> — emphasises Profit, ROI and Margin.</li>
          <li><strong>Renovate to sell</strong> — emphasises Total Reno Cost and Estimated Value Added.</li>
          <li><strong>Renovate to live</strong> — emphasises Total Reno Cost and Cost-per-Room.</li>
        </ul>
      </>
    ),
  },
  {
    cat: "General",
    icon: HelpCircle,
    q: "How does pricing work?",
    a: (
      <>
        FlipCost is a <strong>pay-once unlock</strong>, not a free product.
        For <strong>ZAR 280</strong> you get one full FlipCost project with
        every tab, every room, the Cost Library and the Estimate / Actual
        tracker. Need another property? Buy another single-project unlock.
        Pro &amp; Unlimited tiers (multi-project subscriptions) are launching
        soon — pop your email in the pricing waitlist to lock in early-bird
        pricing. You can <em>try</em> the live demo from the home page
        without an account, but a real project requires the unlock.
      </>
    ),
  },

  {
    cat: "General",
    icon: ImageIcon,
    q: "Can I add photos of the property?",
    a: (
      <>
        Yes. On the Overview tab, click the photo dropzone or drag image
        files straight from your desktop or finder. Files are automatically
        resized to 1600px on the longest edge and re-encoded as JPEG so the
        dashboard stays fast — no URL hunting, no third-party host required.
      </>
    ),
  },

  // --- Accuracy ---------------------------------------------------
  {
    cat: "Accuracy",
    icon: Shield,
    q: "How accurate are the numbers?",
    a: (
      <>
        Built-in rates are <em>approximations</em> drawn from typical labour +
        materials in each region. Real quotes vary by trade availability,
        access, finish brand, and time of year. Use FlipCost to model the
        shape of a deal before you commit; obtain binding quotes before
        signing anything. See the{" "}
        <a className="underline" href="/disclaimer">full disclaimer</a> for
        legal detail.
      </>
    ),
  },
  {
    cat: "Accuracy",
    icon: Sparkles,
    q: "Why does Premium-finish cost so much more than Budget?",
    a: (
      <>
        Finish level applies a multiplier to every interior rate:{" "}
        <strong>Budget × 0.7</strong>, <strong>Mid × 1.0</strong>,{" "}
        <strong>Premium × 1.6</strong>. Exterior &amp; Systems also shift
        scope (Repair / Replace / Upgrade applies <strong>0.4× / 1.0× / 1.4×</strong>).
        That can compound to roughly 2× between Budget and Premium on a
        full-scope renovation — switch a room's finish level on the Rooms
        tab to see the effect live.
      </>
    ),
  },
  {
    cat: "Accuracy",
    icon: HelpCircle,
    q: "My bathroom shows tiling at 2.5× the floor area — is that right?",
    a: (
      <>
        Yes. Bathrooms are typically tiled floor + walls to full height, so
        the template assumes a 2.5× factor over the floor sqm. If your
        bathroom is part-tiled only, set the tiling line item quantity
        manually to match your scope.
      </>
    ),
  },

  // --- Currency & Cost Library --------------------------------------
  {
    cat: "Currency & Library",
    icon: Globe,
    q: "Which currencies are supported?",
    a: (
      <>
        Seven: <strong>USD, GBP, EUR, CAD, AUD, NZD, ZAR</strong>. Each
        project picks one — the selected currency drives the symbol shown
        everywhere and the multiplier applied to built-in (USD-baseline)
        rates so your defaults aren't a literal USD figure.
      </>
    ),
  },
  {
    cat: "Currency & Library",
    icon: Library,
    q: "How do regional presets work?",
    a: (
      <>
        Regional presets (one click on the Cost Library page) seed every
        Rooms / Exterior / Systems rate with a region-specific multiplier on
        top of the USD baseline. They're a starting point, not gospel — the
        whole point of the library is to fine-tune from there. Custom items
        you've added are preserved.
      </>
    ),
  },
  {
    cat: "Currency & Library",
    icon: Library,
    q: "Is the Cost Library shared across all my projects?",
    a: (
      <>
        No — every project carries its <strong>own</strong> Cost Library so
        rates can be tuned per location and never bleed across deals. Open the
        Library tab inside any project to edit it. When you create a new
        project, the New Project dialog offers a "Copy Cost Library
        from…" picker so you can clone rates from another of your projects in
        the same market instead of starting blank.
      </>
    ),
  },
  {
    cat: "Currency & Library",
    icon: Library,
    q: "I changed the Cost Library after creating rooms. Do existing rooms update?",
    a: (
      <>
        No — by design. Your saved rates are only applied to{" "}
        <strong>new</strong> rooms, exterior items, systems items or extension
        shells you add after the change. Existing items keep whatever rate
        they were created with so a mid-build edit never silently rewrites
        your numbers; if you want existing items to reflect the new rate,
        edit the row directly or delete and re-add it.
      </>
    ),
  },
  {
    cat: "Currency & Library",
    icon: Library,
    q: "What does the Undo button on each Cost Library row do?",
    a: (
      <>
        It reverts that row to the regional default for your project's
        currency (e.g. ZAR / sqm rate). The green dot next to the input
        means "you've overridden this" — Undo removes the override. Don't
        forget to hit Save afterwards.
      </>
    ),
  },

  // --- Modes (Estimate vs Actual) -----------------------------------
  {
    cat: "Estimate vs Actual",
    icon: Receipt,
    q: "What's the difference between Estimate and Actual mode?",
    a: (
      <>
        <strong>Estimate</strong> is your plan — every tab models projected
        cost. <strong>Actual</strong> is your tracker — every tab becomes a
        receipt log against the same Rooms / Exterior / Systems / Soft Cost
        buckets. Switch between them with the toggle in the dashboard
        header; the data lives side by side.
      </>
    ),
  },
  {
    cat: "Estimate vs Actual",
    icon: Receipt,
    q: "Can I tag a receipt to a specific room or line item?",
    a: (
      <>
        Yes. Hit "Allocate to a line…" on any entry. For Interior pick a
        Room → Work Item; for Exterior / Systems pick a Category → Variant;
        for Soft Costs pick the line. Unallocated entries still roll up to
        the category total — there's no penalty for skipping it.
      </>
    ),
  },
  {
    cat: "Estimate vs Actual",
    icon: Receipt,
    q: "Why is the Library tab disabled in Actual mode?",
    a: (
      <>
        Once you flip to Actual mode the focus shifts from "what could this
        cost?" to "what did this cost?". Editing rates mid-build would mess
        with your variance numbers, so we hide the Library tab. Flip back to
        Estimate any time to tune rates and the spend log stays intact.
      </>
    ),
  },

  // --- Extensions ---------------------------------------------------
  {
    cat: "Extensions",
    icon: Building2,
    q: "How do extensions work?",
    a: (
      <>
        The Extensions tab supports five common types — single-storey,
        double-storey, loft, garage conversion, knock-through. Each
        extension auto-seeds <strong>shell costs</strong> (foundation,
        frame, roof, glazing, services tie-in, council fees) and lets you
        nest <strong>fitout rooms</strong> inside it. Cost-per-sqm
        automatically uses post-build GLA so the headline number reflects
        what you're selling.
      </>
    ),
  },
  {
    cat: "Extensions",
    icon: Building2,
    q: "Can I have a kitchen inside an extension?",
    a: (
      <>
        Yes — every extension has its own Rooms list nested inside. Drop a
        kitchen + bathroom into your rear extension and they share the same
        Cost Library, currency and finish multipliers as your main rooms.
        No double-entry, no parallel template.
      </>
    ),
  },

  // --- Sharing ------------------------------------------------------
  {
    cat: "Sharing",
    icon: Share2,
    q: "How do I share a project with someone?",
    a: (
      <>
        Hit the <strong>Share</strong> button in the project dashboard header
        (visible to the project owner only). Enter their email, pick a role
        — <strong>View only</strong> or <strong>Can edit</strong> — and click
        Invite. They'll receive an email with a personal invite link; if
        email delivery fails for any reason the invite row also shows a
        "Copy link" button so you can send it manually.
      </>
    ),
  },
  {
    cat: "Sharing",
    icon: Users,
    q: "What's the difference between View only and Can edit?",
    a: (
      <>
        <strong>View only</strong> collaborators can read every tab —
        Overview, Rooms, Extensions, Exterior, Systems, Soft Costs and Library
        — but every input is read-only and a sage banner makes the mode
        obvious. <strong>Can edit</strong> collaborators have full read +
        write access (rooms, rates, photos, actuals) but can't delete the
        project or share it further. Only the original owner sees the Share
        button and the Delete control.
      </>
    ),
  },
  {
    cat: "Sharing",
    icon: Share2,
    q: "When does an invite expire?",
    a: (
      <>
        Invites expire in <strong>7 days</strong>. If a collaborator misses
        the window, open the Share dialog and hit <strong>Resend</strong> on
        their row — that rotates the token (the old link stops working) and
        re-sends the email. You can revoke any invite or accepted share with
        the trash icon at any time, and access is removed instantly.
      </>
    ),
  },
  {
    cat: "Sharing",
    icon: Users,
    q: "How do I tell which projects were shared with me?",
    a: (
      <>
        On the <strong>Projects</strong> list, shared projects show a sage
        "Shared" pill with an eye (View only) or pencil (Can edit) icon. The
        delete trash icon is hidden because only the owner can delete a
        project. Inside the dashboard, view-only collaborators also see a
        sage <em>"View only"</em> banner at the top of the page.
      </>
    ),
  },

  // --- Privacy / Account --------------------------------------------
  {
    cat: "Privacy & Account",
    icon: Shield,
    q: "Who can see my projects?",
    a: (
      <>
        Only you and anyone you've explicitly invited via the Share button.
        Every project is scoped to its owner — regular users see only their
        own data plus any projects shared with them. The admin dashboard
        exists for support &amp; diagnostics; we don't share or sell any
        project content.
      </>
    ),
  },
  {
    cat: "Privacy & Account",
    icon: Shield,
    q: "Can I sign in with Google?",
    a: (
      <>
        Yes. The /auth page offers email + password (bcrypt) and a one-tap
        Google sign-in. If you already have an email account on FlipCost,
        Google sign-in auto-links to it when the email matches.
      </>
    ),
  },
  {
    cat: "Privacy & Account",
    icon: TrendingUp,
    q: "What happens if I delete a project?",
    a: (
      <>
        Deletion is permanent — including any actuals logged against it
        and any pending share invites. There is no soft-delete or recycle
        bin, so make sure you've exported a PDF report first if you want a
        record.
      </>
    ),
  },
  {
    cat: "Privacy & Account",
    icon: Wrench,
    q: "Will my data export be available?",
    a: (
      <>
        Yes — open a project and click <strong>Generate report</strong> in
        the dashboard header to download a multi-page PDF with cost
        breakdowns, room totals and the property snapshot. Excel export is
        on the roadmap. The Comparable Deals panel inside the dashboard is
        the quickest way to size up your numbers against peers in the same
        market.
      </>
    ),
  },
];

const CATS = ["All", "General", "Accuracy", "Currency & Library", "Estimate vs Actual", "Extensions", "Sharing", "Privacy & Account"];

export default function Faq() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("All");
  const [open, setOpen] = useState(() => new Set([0])); // first card open by default
  const [demoOpen, setDemoOpen] = useState(false);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return FAQ.filter((f) => {
      if (cat !== "All" && f.cat !== cat) return false;
      if (!needle) return true;
      const haystack = (f.q + " " + (typeof f.a === "string" ? f.a : "")).toLowerCase();
      return haystack.includes(needle);
    }).map((f, i) => ({ ...f, _idx: FAQ.indexOf(f), _vis: i }));
  }, [query, cat]);

  const toggle = (idx) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  return (
    <div className="min-h-screen fc-app-bg text-[#1C1C1C] fc-fade-in" data-testid="faq-page">
      {/* Header */}
      <header className="sticky top-0 z-30 fc-header-bar">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 py-4 flex items-center justify-between gap-3 sm:gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <button
              data-testid="faq-back-btn"
              onClick={() => navigate(-1)}
              className="w-8 h-8 rounded-lg border border-black/[0.08] grid place-items-center text-[#78716C] hover:text-[#1C1C1C] hover:border-black/[0.2] transition-colors shrink-0"
              aria-label="Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              data-testid="faq-logo-btn"
              onClick={() => navigate("/")}
              className="flex items-baseline gap-2.5 sm:gap-3 group shrink-0 min-w-0"
              aria-label="FlipCost by MyPlanCost — home"
            >
              <span className="fc-heading text-2xl sm:text-[28px] font-extrabold tracking-tight text-[#1C1C1C] leading-none">
                FlipCost
              </span>
              <span className="hidden sm:inline text-[10px] uppercase tracking-[0.25em] text-[#A8A29E] font-medium leading-none whitespace-nowrap">
                by MyPlanCost
              </span>
            </button>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              data-testid="faq-demo-btn"
              onClick={() => setDemoOpen(true)}
              className="h-9 px-3 rounded-lg bg-sage-100 hover:bg-sage-200 border border-sage-200 text-sage-700 text-sm font-medium flex items-center gap-1.5 transition-colors"
            >
              <Play className="w-3.5 h-3.5" /> Try Demo
            </button>
            <button
              data-testid="faq-how-btn"
              onClick={() => navigate("/how-it-works")}
              className="h-9 px-3 rounded-lg border border-black/[0.08] hover:border-black/[0.25] bg-white hover:bg-[#F5F5F4] text-sm text-[#27272A] flex items-center gap-1.5 transition-colors"
            >
              How it works <ArrowRight className="w-4 h-4 text-[#78716C]" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-3 sm:px-6 py-8 sm:py-12 space-y-8">
        {/* Hero */}
        <section data-testid="faq-hero">
          <div className="fc-label mb-3">FAQ</div>
          <h1 className="fc-heading text-4xl sm:text-5xl font-medium tracking-tight text-[#1C1C1C] max-w-3xl leading-tight">
            Quick answers, no fluff.
          </h1>
          <p className="text-base text-[#57534E] mt-5 max-w-2xl leading-relaxed">
            Filter by topic, search by keyword, or scroll the list. Can't find what you're looking for? The{" "}
            <button
              type="button"
              onClick={() => navigate("/how-it-works")}
              className="underline hover:text-[#1C1C1C]"
            >
              How it works
            </button>{" "}
            page covers the full flow.
          </p>
        </section>

        {/* Search + category chips */}
        <section className="fc-card p-4 sm:p-5 space-y-4" data-testid="faq-controls">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29E]" />
            <Input
              data-testid="faq-search"
              type="text"
              placeholder="Search the FAQ…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="fc-input h-10 rounded-md text-sm pl-9"
            />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="faq-cats">
            {CATS.map((c) => {
              const active = cat === c;
              return (
                <button
                  key={c}
                  type="button"
                  data-testid={`faq-cat-${c.replace(/[^a-zA-Z0-9]/g, "-")}`}
                  onClick={() => setCat(c)}
                  className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${
                    active
                      ? "bg-[#1C1C1C] text-white border-[#1C1C1C]"
                      : "bg-white text-[#57534E] border-black/[0.08] hover:border-black/[0.25] hover:bg-[#F5F5F4]"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </section>

        {/* Question list */}
        <section className="space-y-3" data-testid="faq-list">
          {filtered.length === 0 && (
            <div
              className="fc-card p-8 text-center text-sm text-[#78716C]"
              data-testid="faq-empty"
            >
              No questions match "{query}". Try a different search term or clear the filter.
            </div>
          )}
          {filtered.map((f) => {
            const Icon = f.icon;
            const isOpen = open.has(f._idx);
            return (
              <article
                key={f._idx}
                className="fc-card overflow-hidden"
                data-testid={`faq-item-${f._idx}`}
              >
                <button
                  type="button"
                  data-testid={`faq-item-toggle-${f._idx}`}
                  onClick={() => toggle(f._idx)}
                  aria-expanded={isOpen}
                  className="w-full text-left px-5 py-4 flex items-center gap-4"
                >
                  <div className="shrink-0 w-9 h-9 rounded-lg bg-sage-100 border border-sage-200 grid place-items-center text-sage-700">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-sage-700">
                      {f.cat}
                    </div>
                    <div className="fc-heading text-[15px] text-[#1C1C1C] mt-0.5">{f.q}</div>
                  </div>
                  <ChevronDown
                    className={`w-4 h-4 text-[#78716C] transition-transform shrink-0 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isOpen && (
                  <div
                    className="px-5 pb-5 pl-[72px] text-[14px] text-[#57534E] leading-relaxed"
                    data-testid={`faq-item-answer-${f._idx}`}
                  >
                    {f.a}
                  </div>
                )}
              </article>
            );
          })}
        </section>

        {/* Footer CTA */}
        <section className="fc-card p-8 text-center" data-testid="faq-cta">
          <h3 className="fc-heading text-xl text-[#1C1C1C]">Still have a question?</h3>
          <p className="text-sm text-[#57534E] mt-2 max-w-md mx-auto">
            Open the live demo to see the answer in context — or jump to the How-it-works page for the full walkthrough.
          </p>
          <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
            <button
              data-testid="faq-cta-demo-btn"
              onClick={() => setDemoOpen(true)}
              className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-lg h-10 px-5 text-sm font-medium inline-flex items-center gap-1.5 transition-colors"
            >
              <Play className="w-3.5 h-3.5" /> Try the Live Demo
            </button>
            <button
              data-testid="faq-cta-how-btn"
              onClick={() => navigate("/how-it-works")}
              className="rounded-lg h-10 px-5 text-sm font-medium inline-flex items-center gap-1.5 border border-black/[0.08] hover:border-black/[0.25] bg-white hover:bg-[#F5F5F4] text-[#1C1C1C] transition-colors"
            >
              See how it works <ArrowRight className="w-4 h-4" />
            </button>
            <button
              data-testid="faq-cta-start-btn"
              onClick={() => navigate("/projects?new=1")}
              className="rounded-lg h-10 px-5 text-sm font-medium inline-flex items-center gap-1.5 border border-black/[0.08] hover:border-black/[0.25] bg-white hover:bg-[#F5F5F4] text-[#1C1C1C] transition-colors"
            >
              Start a project
            </button>
          </div>
        </section>

        <DisclaimerBar />
      </main>
      <DemoDialog open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
}
